import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../services/api";
import "./Login.css";

const Login = () => {
  const [formData, setFormData] = useState({
    identifier: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const shouldSkipSessionCheck = Boolean(location.state?.skipSessionCheck);

  useEffect(() => {
    document.title = "Login | Balitah";
  }, []);

  useEffect(() => {
    if (shouldSkipSessionCheck) {
      setIsCheckingSession(false);
      return;
    }

    let isMounted = true;

    const checkExistingSession = async () => {
      try {
        const response = await api.getMe();
        if (!isMounted) {
          return;
        }

        if (response?.success && response?.data) {
          const user = response.data.user || response.data;
          const role = String(user.role || "")
            .trim()
            .toLowerCase();

          if (role === "admin") {
            navigate("/admin", { replace: true });
            return;
          }

          if (role === "seller") {
            navigate("/seller", { replace: true });
            return;
          }

          navigate("/products", { replace: true });
          return;
        }
      } catch (err) {
        // User is not authenticated; keep login page visible.
      } finally {
        if (isMounted) {
          setIsCheckingSession(false);
        }
      }
    };

    checkExistingSession();

    return () => {
      isMounted = false;
    };
  }, [navigate, shouldSkipSessionCheck]);

  if (isCheckingSession) {
    return null;
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(""); // Clear error on input
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    if (!formData.identifier.trim()) {
      setError("Please enter email or name");
      setIsLoading(false);
      return;
    }

    if (!formData.password) {
      setError("Please enter password");
      setIsLoading(false);
      return;
    }

    try {
      // Send both credential and password to backend
      const response = await api.login({
        credential: formData.identifier,
        password: formData.password,
      });

      if (response.success) {
        // Redirect to dashboard
        const userRole = response.data?.user?.role;
        if (userRole === "admin") {
          navigate("/admin");
        } else if (userRole === "seller") {
          navigate("/seller");
        } else {
          navigate("/products");
        }
      } else {
        setError(response.message || "Login failed");
      }
    } catch (err) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Left Side - Form */}
      <div className="login-form-section">
        <div className="login-form-wrapper">
          {/* Logo */}
          <div className="login-logo">
            <Link to="/" className="login-logo-link" aria-label="Go to home">
              <h1 className="login-logo-title">
                <img
                  src="/favicon-48.png"
                  alt="Balitah logo"
                  className="login-logo-icon"
                />
                BALITAH
              </h1>
            </Link>
            <div className="logo-accent"></div>
          </div>

          <p className="login-welcome">
            Welcome back! Please sign in to continue.
          </p>

          {/* Error Message */}
          {error && <div className="login-error">{error}</div>}

          <form onSubmit={handleSubmit} className="login-form">
            {/* Email/Name */}
            <div className="input-group">
              <label htmlFor="identifier">Email or Name</label>
              <div className="input-wrapper">
                <svg
                  className="input-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <input
                  type="text"
                  id="identifier"
                  name="identifier"
                  value={formData.identifier}
                  onChange={handleChange}
                  placeholder="Enter your email or name"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="input-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <svg
                  className="input-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="eye-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className={`submit-btn ${isLoading ? "loading" : ""}`}
              disabled={isLoading}
            >
              {isLoading ? <span className="spinner"></span> : "Sign In"}
            </button>

            {/* Sign Up */}
            <p className="signup-text">
              New to Balitah?{" "}
              <Link to="/register" className="signup-link">
                Create Account
              </Link>
            </p>
          </form>
        </div>
      </div>

      {/* Right Side - Branding */}
      <div className="login-brand-section">
        <div className="brand-content">
          <h2>Welcome Back to Balitah</h2>
          <p>
            Shop local Bruneian products easily and securely with our trusted
            platform.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
